// Builds a printable/emailable Production Requirement document (HTML string).
// Uses Company Profile for the header. No transaction is created by print/email.
import { planCode } from "@/lib/production";

const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
const n3 = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

export function buildRequirementHtml(plan, company = {}, rows = [], readiness = "") {
  const lines = Array.isArray(plan?.lines) ? plan.lines : [];
  const logo = company.logoDataUri ? `<img src="${company.logoDataUri}" style="height:54px"/>` : "";
  const prodLines = lines.map((l) => `
    <tr><td>${esc(l.productId)}</td><td>${esc(l.productName)}</td><td>v${esc(l.recipeVersion)}</td>
    <td style="text-align:right">${n3(l.plannedQty)} ${esc(l.unit || "")}</td></tr>`).join("");
  const reqRows = rows.map((r) => `
    <tr>
      <td>${esc(r.name)}</td>
      <td style="text-align:right">${n3(r.requiredQty)} ${esc(r.unit || "")}</td>
      <td style="text-align:right">${n3(r.current)}</td>
      <td style="text-align:right">${n3(r.reserved)}</td>
      <td style="text-align:right">${n3(r.available)}</td>
      <td style="text-align:right;color:${r.shortfall > 0 ? "#b91c1c" : "#15803d"}">${r.shortfall > 0 ? n3(r.shortfall) : "0"}</td>
    </tr>`).join("");
  const badgeColor = readiness === "READY" ? "#15803d" : readiness === "NOT READY" ? "#b91c1c" : "#b45309";
  return `<!doctype html><html><head><meta charset="utf-8"/><title>Production Requirement ${esc(planCode(plan))}</title>
  <style>
    *{font-family:Arial,Helvetica,sans-serif;color:#1c1917}
    body{margin:28px}
    h1{font-size:18px;margin:0}
    table{width:100%;border-collapse:collapse;margin-top:10px;font-size:12px}
    th,td{border:1px solid #d6d3d1;padding:6px 8px;text-align:left}
    th{background:#f5f5f4;font-size:11px;text-transform:uppercase}
    .muted{color:#78716c;font-size:12px}
    .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #b45309;padding-bottom:10px}
    .badge{display:inline-block;padding:3px 10px;border-radius:4px;color:#fff;font-size:12px;font-weight:bold;background:${badgeColor}}
    .grid{display:grid;grid-template-columns:1fr 1fr;gap:2px 24px;margin-top:8px;font-size:12px}
  </style></head><body>
  <div class="head">
    <div>${logo}<h1>${esc(company.legalName || company.tradeName || "Production Requirement")}</h1>
      <div class="muted">${esc(company.registeredAddress || "")}</div>
      <div class="muted">GSTIN: ${esc(company.gstin || "-")} · FSSAI: ${esc(company.fssaiLicenceNo || "-")}</div>
    </div>
    <div style="text-align:right">
      <div style="font-size:15px;font-weight:bold">PRODUCTION REQUIREMENT</div>
      <div class="muted">${esc(planCode(plan))}</div>
      <div class="muted">Production Date: ${esc(plan?.date || "-")}</div>
      <div class="muted">Generated: ${new Date().toLocaleString()}</div>
    </div>
  </div>

  <div class="grid">
    <div>Section: <b>${esc(plan?.section || "-")}</b></div>
    <div>Status: <b>${esc(plan?.status || "-")}</b></div>
    <div>Readiness: <span class="badge">${esc(readiness || "-")}</span></div>
  </div>

  <h3 style="margin:16px 0 0;font-size:13px">Products</h3>
  <table><thead><tr><th>Product ID</th><th>Product</th><th>Recipe</th><th style="text-align:right">Planned Qty</th></tr></thead>
  <tbody>${prodLines || '<tr><td colspan="4">—</td></tr>'}</tbody></table>

  <h3 style="margin:16px 0 0;font-size:13px">Ingredient Requirement vs Store Stock</h3>
  <table><thead><tr><th>Ingredient</th><th style="text-align:right">Required</th><th style="text-align:right">Current</th>
  <th style="text-align:right">Reserved</th><th style="text-align:right">Available</th><th style="text-align:right">Shortfall</th></tr></thead>
  <tbody>${reqRows || '<tr><td colspan="6">No ingredient requirements</td></tr>'}</tbody></table>

  <p class="muted" style="margin-top:24px">Prepared for Stores / Production. This requirement is a projection and does not create any procurement transaction.</p>
  </body></html>`;
}
